import styled, { useTheme } from 'styled-components';
import Modal from '../../../components/Modal';
import { BodyLarge, ParagraphLargeText } from '../../../components/Texts';
import { useSolana } from '../../../hooks/useSolana';
import Button from '../../../components/Button';
import { openCenteredWindow } from '../../../utils/window';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import { useStartWithdrawalMutation } from '../../../redux/api/authorized/withdrawal';
import { useState, useEffect } from 'react';
import TextInput from '../../../components/Inputs/Text';
import AmountInput from '../../../components/Inputs/Amount';
import { Controller, useForm } from 'react-hook-form';
import {
  selectTokenBySymbol,
  selectTokensWithUSDBalance,
} from '../../../redux/slices/tokenSlice';
import { selectTokenBalance } from '../../../redux/slices/walletSlice';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { PublicKey } from '@solana/web3.js';
import { toast } from 'react-toastify';
import { toTokenAmount } from '../../../hooks/useSolana';
import BridgeOfframpModal from '../BridgeOfframp';
import { useFlag } from '@unleash/proxy-client-react';
import {
  BRIDGE,
  COINBASE,
  EVENT_WITHDRAW_OPTION_CLICKED,
  sendEvent,
  WEB3_WALLET,
} from '../../../common/analytics';
import { truncateAddress } from '../../../components/WalletConnect';
import TokenSelector from './TokenSelector';
import { openModal } from '../../../redux/slices/modals';
import { Capacitor } from '@capacitor/core';

const ContentContainer = styled.div`
  width: 100%;
  overflow: hidden;
`;

const StyledModal = styled(Modal)`
  .modal-content {
    overflow: hidden;
    max-height: 700px;
    ${PHONE_MEDIA_QUERY} {
      max-height: unset;
      height: fit-content;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 16px 0px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
`;

const ConfirmationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 10px 0px;
`;

const ConfirmationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.border.primary};
  gap: 16px;

  &:last-child {
    border-bottom: none;
  }
`;

const ConfirmationLabel = styled(BodyLarge)`
  color: ${({ theme }) => theme.text.medium};
  flex-shrink: 0;
`;

const ConfirmationValue = styled(BodyLarge)`
  font-weight: 500;
  word-break: break-all;
  text-align: right;
`;

const isValidSolanaAddress = (address) => {
  try {
    new PublicKey(address);
    return true;
  } catch (e) {
    return false;
  }
};

const WithdrawFundsModal = ({
  onClose,
  initialSelectedToken = 'USDC',
  showBackButton = false,
}) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const { transferToken, loading, primaryWallet, refreshTokenBalances } =
    useSolana();
  const [startWithdrawal] = useStartWithdrawalMutation();
  const [mode, setMode] = useState(null);
  const theme = useTheme();
  const [transferData, setTransferData] = useState(null);
  const [showBridgeOfframp, setShowBridgeOfframp] = useState(false);
  const bridgeOfframpEnabled = useFlag('bridgeOfframpEnabled');
  const tokens = useSelector((state) => selectTokensWithUSDBalance(state));
  const dispatch = useDispatch();

  const [selectedToken, setSelectedToken] = useState(initialSelectedToken);
  const [selectedTokenBalance, setSelectedTokenBalance] = useState(0);

  const usdcToken = useSelector((state) =>
    selectTokenBySymbol(state, 'solana', 'USDC'),
  );

  useEffect(() => {
    if (tokens.length > 0) {
      if (!tokens.find((token) => token.symbol === initialSelectedToken)) {
        setSelectedToken(tokens[0].symbol);
      } else {
        console.log('keeping existing token');
        setSelectedToken(initialSelectedToken);
      }
    }
  }, [tokens]);

  useEffect(() => {
    const token = tokens.find((token) => token.symbol === selectedToken);
    setSelectedTokenBalance(token?.balance || 0);
    setValue('amount', '0');
    if (selectedToken === 'USDC') {
      setMode(null);
    } else {
      setMode('transfer');
    }
  }, [selectedToken]);

  const balance =
    useSelector((state) =>
      selectTokenBalance('solana', usdcToken?.address || '')(state),
    ) || 0;

  const schema = yup.object({
    amount: yup
      .number()
      .test('max-balance', 'Amount exceeds your balance', (value) => {
        if (!value) return true;
        const numValue = parseFloat(value);
        const numBalance = selectedTokenBalance;
        return numValue <= numBalance;
      })
      .required('Amount is required')
      .typeError('Invalid amount'),
    address: yup.string().when('$mode', {
      is: 'transfer',
      then: (schema) =>
        schema
          .required('Address is required')
          .test('is-valid-solana-address', 'Invalid Solana address', (value) =>
            value ? isValidSolanaAddress(value) : false,
          ),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    defaultValues: {
      amount: '',
      address: '',
      mode: null,
    },
    resolver: yupResolver(schema),
    context: { mode },
  });

  useEffect(() => {
    setValue('mode', mode);
  }, [mode, setValue]);

  const handleMaxAmount = async () => {
    setValue('amount', selectedTokenBalance.toString());
    await trigger('amount');
  };

  const handleConfirmTransfer = async () => {
    const token = tokens.find((token) => token.symbol === selectedToken);
    const { error } = await transferToken(
      token.symbol,
      transferData.amount,
      primaryWallet,
      transferData.address,
    );
    if (!error) {
      onClose();
    }
  };

  const onSubmit = async (data) => {
    sendEvent(EVENT_WITHDRAW_OPTION_CLICKED, {
      provider: COINBASE,
    });
    if (mode === 'transfer') {
      setTransferData(data);
      setMode('confirm');
    } else {
      const onrampUrl = await startWithdrawal({
        network: 'solana',
        fiatAmount: data.amount,
      }).unwrap();
      openCenteredWindow(onrampUrl, {}, isMobile);
    }
  };

  const handleTransferToWallet = () => {
    sendEvent(EVENT_WITHDRAW_OPTION_CLICKED, {
      provider: WEB3_WALLET,
    });
    setMode('transfer');
    setValue('address', '');
    setTransferData(null);
  };

  const handleBridgeOfframp = () => {
    sendEvent(EVENT_WITHDRAW_OPTION_CLICKED, {
      provider: BRIDGE,
    });
    setShowBridgeOfframp(true);
  };

  const handleBack = () => {
    if (mode === 'confirm') {
      setMode('transfer');
    } else {
      setMode(null);
      setValue('address', '');
      setTransferData(null);
    }
  };

  return (
    <StyledModal
      onClose={onClose}
      initialBreakpoint={1}
      breakpoints={[0, 0.5, 1]}
      customStyle={{ minHeight: '530px' }}
      onBack={
        showBackButton
          ? () => dispatch(openModal({ name: 'withdrawOrDeposit' }))
          : undefined
      }
    >
      <ContentContainer>
        {showBridgeOfframp ? (
          <BridgeOfframpModal
            onClose={() => setShowBridgeOfframp(false)}
            amount={transferData?.amount}
          />
        ) : (
          <>
            {mode !== 'confirm' && (
              <>
                <ParagraphLargeText
                  style={{ textAlign: 'center', color: theme.text.title }}
                >
                  Withdraw Funds
                </ParagraphLargeText>
              </>
            )}

            {mode === 'confirm' && transferData ? (
              <ConfirmationContainer>
                <ParagraphLargeText tyle={{ textAlign: 'center' }}>
                  Confirm Transfer
                </ParagraphLargeText>
                <BodyLarge tyle={{ textAlign: 'center' }}>
                  Please review your transfer details
                </BodyLarge>

                <ConfirmationRow>
                  <ConfirmationLabel>Amount</ConfirmationLabel>
                  <ConfirmationValue>
                    {transferData.amount} {selectedToken}
                  </ConfirmationValue>
                </ConfirmationRow>

                <ConfirmationRow>
                  <ConfirmationLabel>Recipient Address</ConfirmationLabel>
                  <ConfirmationValue>
                    {truncateAddress(transferData.address)}
                  </ConfirmationValue>
                </ConfirmationRow>

                <Button
                  type='button'
                  fullWidth={true}
                  disabled={loading}
                  onClick={handleConfirmTransfer}
                >
                  Confirm Transfer
                </Button>

                <Button
                  type='button'
                  variant='secondary'
                  fullWidth={true}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </ConfirmationContainer>
            ) : (
              <>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <div
                    style={{
                      marginLeft: '1px',
                      marginRight: '1px',
                      marginTop: '16px',
                    }}
                  >
                    <TokenSelector
                      onSelectionChange={() => {}}
                      options={tokens}
                      value={selectedToken}
                      onChange={setSelectedToken}
                    />
                  </div>
                  {(!Capacitor.isNativePlatform() ||
                    (Capacitor.isNativePlatform() && mode === 'transfer')) && (
                    <Controller
                      name='amount'
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <AmountInput
                          label={`Amount (${selectedToken})`}
                          placeholder='Enter amount'
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          error={errors.amount?.message}
                          onMaxClick={handleMaxAmount}
                        />
                      )}
                    />
                  )}

                  {mode === 'transfer' && (
                    <Controller
                      name='address'
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <TextInput
                          label='Recipient Address'
                          placeholder='Enter Solana address'
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          error={errors.address?.message}
                        />
                      )}
                    />
                  )}

                  {selectedToken === 'USDC' && (
                    <>
                      {!Capacitor.isNativePlatform() && (
                        <Button
                          type='submit'
                          fullWidth={true}
                          disabled={!isValid || loading}
                        >
                          {mode === 'transfer'
                            ? 'Review Transfer'
                            : 'Withdraw to Coinbase'}
                        </Button>
                      )}
                    </>
                  )}

                  {selectedToken !== 'USDC' && (
                    <>
                      {!Capacitor.isNativePlatform() && (
                        <Button
                          type='submit'
                          fullWidth={true}
                          disabled={!isValid || loading}
                        >
                          Review Transfer
                        </Button>
                      )}
                    </>
                  )}

                  {Capacitor.isNativePlatform() && mode === 'transfer' && (
                    <Button
                      type='submit'
                      fullWidth={true}
                      disabled={!isValid || loading}
                    >
                      transfer
                    </Button>
                  )}

                  {mode === 'transfer' && selectedToken === 'USDC' && (
                    <Button
                      type='button'
                      variant='secondary'
                      fullWidth={true}
                      onClick={handleBack}
                    >
                      Back
                    </Button>
                  )}
                </Form>

                {!mode && (
                  <ButtonContainer>
                    <Button
                      type='button'
                      onClick={handleTransferToWallet}
                      variant='secondary'
                      fullWidth={true}
                    >
                      Transfer to another wallet
                    </Button>
                  </ButtonContainer>
                )}

                {selectedToken === 'USDC' && (
                  <>
                    {!mode && (
                      <ButtonContainer>
                        {bridgeOfframpEnabled && (
                          <Button
                            type='button'
                            onClick={handleBridgeOfframp}
                            variant={
                              Capacitor.isNativePlatform()
                                ? 'primary'
                                : 'secondary'
                            }
                            fullWidth={true}
                          >
                            Transfer to Bank Account
                          </Button>
                        )}
                      </ButtonContainer>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </ContentContainer>
    </StyledModal>
  );
};

export default WithdrawFundsModal;
